// homeSignals.js — WHAT NEEDS THE PERSON, from every installed app: the shell
// side of HOME_SIGNALS_CONTRACT v1 (tickets/reference/white-label-redesign/
// HOME_SIGNALS_CONTRACT.md; DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1).
//
// An app answers `homeSignals(ctx)` → Promise<Signal[]> — a module function
// (Frank-discovered `functions/homeSignals.js`), reached through the app's OWN
// module context, the same carrier the ai-boards vocabulary and the home board
// use for an app's widgets (`root._moduleDsCtxGet(appId).functions`, own
// properties only: the context chains to the shell's merged bag, and a
// sibling app's `homeSignals` must never answer for this one). Backend reads
// inside a provider go through @symbo.ls/sdk (`this.sdk` / `ctx.sdk`).
//
//   Signal = { id: '<app>.<subject>.<kind>', app, kind, count >= 1,
//              severity: 'urgent'|'attention'|'info',
//              label: { key, params }, href, at?, notifFilter? }
//
// `notifFilter` ('all' | 'unread' | 'workspace', the root's `_notifFilter`)
// rides only a signal that opens the Inbox: the Inbox filter that lists the
// rows the signal counted. homeSignalsOpen sets it before it opens the list.
//
// THE RULES THIS FILE ENFORCES (contract §2):
//   • counts only — a signal carries no names and no record text; an invalid
//     or zero signal is dropped; at most 5 per app, sorted inside the app by
//     severity then count; `app` is the provider the shell called, whatever
//     the provider wrote;
//   • BUDGET: a pass waits at most 800 ms for its providers. A provider still
//     out after that is skipped for this render and lands on the next one; a
//     call still out after 10 s reads as `[]` (a hang must not hold the line
//     forever). A throw or a rejection reads as `[]` and logs once;
//   • CACHE: per workspace, 60 s, in memory (`root._homeSignals`, workspace-
//     scoped — a switch wipes it, and a late answer for the previous
//     workspace drops itself). `el.call('homeSignalsInvalidate', app)` from a
//     mutation site refreshes at once;
//   • ORDER: severity (urgent > attention > info), then count, then the app's
//     place in the App strip;
//   • "Nothing needs you right now" only when EVERY installed provider has
//     answered and none emitted a signal; while providers load, the line waits.
//
// THE BUILT-IN PROVIDERS move onto the same contract: tasks (open, overdue),
// calendar (today's events), mail (unread), inbox (unread notifications) — the counts the greeting showed as
// a sentence, now as signals. They read the shell's own loaded state and are
// READY only once their source has answered (a first-paint "0 tasks" would be a
// false claim); a source still silent after 5 s answers with what it has.
//
// FRANK: every export is self-contained (FA204) — the vocabularies and the
// small helpers are restated where they are used; siblings are reached through
// `this.call`. No module state: the cache is the workspace-scoped root key.

import { installedAppRefs } from './workspaceConfigs.js'

// ── Pure: one app's list → valid signals, sorted inside the app, at most 5 ──
export const homeSignalsNormalize = function homeSignalsNormalize(list, app) {
  const KINDS = ['overdue', 'dueToday', 'upcoming', 'waiting', 'new', 'risk']
  const RANK = { urgent: 0, attention: 1, info: 2 }
  // The Inbox's filters (the root's `_notifFilter`, shared state.js).
  const NOTIF_FILTERS = ['all', 'unread', 'workspace']
  if (!Array.isArray(list) || typeof app !== 'string' || !app) return []
  const out = []
  const seen = new Set()
  for (const s of list) {
    if (!s || typeof s !== 'object') continue
    const count = Math.floor(Number(s.count))
    if (!Number.isFinite(count) || count < 1) continue
    if (!KINDS.includes(s.kind) || !(s.severity in RANK)) continue
    const label = s.label && typeof s.label === 'object' ? s.label : null
    if (!label || typeof label.key !== 'string' || !label.key) continue
    const id = typeof s.id === 'string' && s.id ? s.id : app + '.' + label.key + '.' + s.kind
    if (seen.has(id)) continue
    seen.add(id)
    const params = {}
    const raw = label.params && typeof label.params === 'object' ? label.params : {}
    for (const [k, v] of Object.entries(raw)) {
      if (typeof v === 'number' || typeof v === 'string') params[k] = v
    }
    const at = typeof s.at === 'string' && !Number.isNaN(new Date(s.at).getTime()) ? s.at : null
    const href = typeof s.href === 'string' && s.href.charAt(0) === '/' ? s.href : null
    const sig = {
      id,
      app,
      kind: s.kind,
      count,
      severity: s.severity,
      label: { key: label.key, params },
      href,
      at
    }
    // A filter for the list it opens, and only the Inbox has one: any other
    // signal never moves the Inbox's filter.
    if (href && href.split(/[?#]/)[0] === '/inbox' && NOTIF_FILTERS.includes(s.notifFilter)) {
      sig.notifFilter = s.notifFilter
    }
    out.push(sig)
  }
  out.sort((a, b) => RANK[a.severity] - RANK[b.severity] || b.count - a.count)
  return out.slice(0, 5)
}

// ── A signal's words: the label's template with its params ─────────────────
// `opts.bare`: the words WITHOUT the count — a tile shows the count as its
// numeral and these words under it ("47" / "follow-ups overdue"). The count is
// removed from the rendered words once, at a word boundary; words that do not
// carry it are returned whole.
export const homeSignalsText = function homeSignalsText(sig, opts) {
  if (!sig || !sig.label) return ''
  const el = this
  const key = sig.label.key
  const template =
    el && typeof el.call === 'function' ? String(el.call('polyglot', key) ?? key) : String(key)
  const params = sig.label.params || {}
  const text = template.replace(/\{([a-zA-Z]+)\}/g, (m, name) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : m
  )
  if (!opts || !opts.bare) return text
  const n = String(sig.count)
  const m = new RegExp('(^|[^0-9])(' + n + ')(?![0-9])').exec(text)
  if (!m) return text
  const at = m.index + m[1].length
  return (text.slice(0, at) + text.slice(at + n.length)).replace(/\s{2,}/g, ' ').trim()
}

// ── The providers of this workspace ──────────────────────────────────────────
// → { known, providers: [{ app, appId, ready, run }], failed: [appId] }.
// `known` is false while the workspace's record or its installed apps are
// still on their way: nobody can say "nothing" before every provider is known.
// `failed` names the installed apps the loader says will NOT come — they are
// known WITHOUT data: they never hold the summary in its skeleton, and
// "nothing" is never said over them. `this` is the element (el.call).
export const homeSignalsProviders = function homeSignalsProviders(root) {
  const el = this
  const r = root && typeof root === 'object' ? root : {}
  const call = (name, ...args) =>
    el && typeof el.call === 'function' ? el.call(name, ...args) : undefined
  const providers = []
  const ctxFor = (extra) => ({
    root: r,
    sdk: (el && el.sdk) || null,
    workspaceId: (r.workspace && String(r.workspace.id || r.workspace._id || '')) || null,
    lang: r.lang || 'en',
    now: Date.now(),
    ...extra
  })

  // Built-ins — the shell's own apps, over the shell's loaded state.
  const ticketsReady =
    Array.isArray(r._userTickets) ||
    !!r._homeTicketsError ||
    (r._dataLoading === false && !r.currentUserEmail)
  providers.push({
    app: 'tasks',
    appId: null,
    ready: ticketsReady,
    run: () => call('homeSignalsTasks', ctxFor({ app: 'tasks' }))
  })
  providers.push({
    app: 'calendar',
    appId: null,
    ready: !!(r.intranetEventsLoaded || r.intranetEventsError),
    run: () => call('homeSignalsCalendar', ctxFor({ app: 'calendar' }))
  })
  if (call('navItemVisible', r, '/mail')) {
    const counts =
      r.navUnreadCounts && typeof r.navUnreadCounts === 'object' ? r.navUnreadCounts : {}
    providers.push({
      app: 'mail',
      appId: null,
      ready: counts.mail !== undefined && counts.mail !== null,
      run: () => call('homeSignalsMail', ctxFor({ app: 'mail' }))
    })
  }
  // Inbox — the notification centre's unread rows of this workspace (persona
  // round 5, marcus: "Nothing needs you right now" over an unread lead). A
  // built app pings its owner ONLY through notifications, so this is where
  // its news reaches the home. Ready once the centre answered (`notifsLoaded`).
  providers.push({
    app: 'inbox',
    appId: null,
    ready: !!r.notifsLoaded,
    run: () => call('homeSignalsInbox', ctxFor({ app: 'inbox' }))
  })

  // Installed apps — the takeover module first, then the additive apps, one
  // provider per app, through the app's OWN module context.
  // Only INSTALLED APP entries wait for the published list — the loader's own
  // rule (workspaceConfigs.js installedAppRefs). A records-grant row
  // (`type: 'legacy-backfill'`) or any other non-app row is never loaded, so
  // it never publishes: counting it held the line "not known" for nothing.
  //
  // THE WORKSPACE RECORD FIRST (crm-w1, dev 3e103946b, the Unreal twin's cold
  // load: "Nothing needs you right now." sat in the page text from 11.6 s to
  // 12.9 s under the boot loader). Before the full record arrives the
  // `settings` bag is absent and the installed-apps list reads [] — "no apps",
  // a lie. The record is LOADED when it carries its settings bag or the
  // full-record stamp (shared workspaceSettings.js: a list / claims row never
  // carries settings). While an id is known and the completion is in flight
  // the apps are NOT known. A completion that definitively failed
  // (`_completionUnreachable`) is terminal — nothing more is coming, so the
  // default (no apps) is the honest answer and nothing waits forever.
  const w = r.workspace
  const settingsReady =
    !!w && (w._shape === 'full' || (!!w.settings && typeof w.settings === 'object'))
  const settingsPending = !settingsReady && !!w && !!(w.id || w._id) && !w._completionUnreachable
  const settings = (settingsReady && w.settings) || {}
  const lc = (v) => (v == null ? '' : String(v).toLowerCase())
  const refs = installedAppRefs(settings)
  const installed = new Set(refs.map((ref) => lc(ref.owner) + '/' + lc(ref.key)))
  const published = Array.isArray(r.workspaceApps) ? r.workspaceApps : null
  const appsKnown = !refs.length || !!published
  // THE APPS THE LOADER SAYS WILL NOT COME — known WITHOUT data. Its record of
  // this workspace's apps phase (functions/workspaceProjects.js
  // updateWorkspaceActivation) names the refused ones (`blocked`, settled); a
  // finished pass that reported a failure ('partial' | 'error' | 'blocked')
  // did not load the installed apps missing from the published list; a
  // published entry may say `loadFailed`. A failed takeover module (its phase
  // 'error') is the same. None of them waits forever, and none lets the home
  // say "nothing" — its items are unknown.
  const wsKey = (w && String(w.id || w._id || '')) || ''
  const act = r._workspaceActivation
  const phase = (name) =>
    act &&
    typeof act === 'object' &&
    String(act.workspaceId || '') === wsKey &&
    act[name] &&
    typeof act[name] === 'object'
      ? act[name]
      : null
  const appsPhase = phase('apps')
  const blocked = new Set(
    (appsPhase && Array.isArray(appsPhase.blocked) ? appsPhase.blocked : []).map(lc)
  )
  const passFailed = !!appsPhase && ['partial', 'error', 'blocked'].includes(appsPhase.state)
  const publishedIds = new Set()
  for (const a of published || []) {
    if (a && a.appId) publishedIds.add(lc(a.appId))
    if (a && a.id) publishedIds.add(lc(a.id))
  }
  const failed = []
  for (const id of installed) {
    if (blocked.has(id) || (published && passFailed && !publishedIds.has(id))) failed.push(id)
  }
  const modulePhase = phase('module')
  const moduleFailed =
    !!settings.workspaceModule &&
    !r.workspaceProject &&
    !!modulePhase &&
    modulePhase.state === 'error'
  if (moduleFailed) failed.push('module')
  const moduleKnown = !settings.workspaceModule || !!r.workspaceProject || moduleFailed
  const seen = new Set()
  const apps = []
  const wp = r.workspaceProject
  if (wp && wp.id) apps.push(wp)
  for (const a of Array.isArray(r.workspaceApps) ? r.workspaceApps : []) apps.push(a)
  const getCtx = typeof r._moduleDsCtxGet === 'function' ? r._moduleDsCtxGet : null
  const getState = typeof r._moduleStateGet === 'function' ? r._moduleStateGet : null
  let holding = false
  for (const appEntry of apps) {
    const appId = appEntry && appEntry.id ? String(appEntry.id) : ''
    if (!appId || seen.has(appId)) continue
    seen.add(appId)
    const isModule = appEntry === wp
    const keys = [lc(appEntry.appId), lc(appId)].filter(Boolean)
    // Does it FEED the Signals? The loader publishes the home providers an app
    // has at the first apps pass (`homeProviders`: 'signals' / 'upcoming'; [] =
    // it never feeds the home); an entry without the field (an older loader)
    // is taken to feed them.
    const feeds = appEntry.homeProviders
    const feedsSignals = !Array.isArray(feeds) || feeds.includes('signals')
    // The loader gave up on it — refused (`apps.blocked`), `loadFailed`, or its
    // setup FAILED (`setupFailed: true`, pendingWidgets null; its placeholder
    // keeps the error): known WITHOUT data. Never asked (its provider would
    // read the SHELL root and answer a wrong "0 follow-ups"), never waited
    // for, and when it feeds the Signals the home never says "nothing" over it.
    if (
      !isModule &&
      (appEntry.loadFailed === true ||
        appEntry.setupFailed === true ||
        keys.some((k) => blocked.has(k)))
    ) {
      if (feedsSignals && !keys.some((k) => failed.includes(k))) failed.push(keys[0])
      continue
    }
    // Still SETTING UP — its published entry carries `pendingWidgets` as an
    // array (the boot reveals the home before the apps finish): it has no state
    // root yet, so it is never asked, and it holds the line back from "nothing"
    // only when it feeds the Signals. An entry the workspace no longer INSTALLS
    // (uninstalled during its setup; the loader drops it on its next pass) is
    // nothing to wait for.
    if (Array.isArray(appEntry.pendingWidgets)) {
      if (!isModule && !keys.some((k) => installed.has(k))) continue
      if (feedsSignals) holding = true
      continue
    }
    const modCtx = getCtx ? getCtx(appId) : null
    const fns = modCtx && modCtx.functions
    const own =
      fns && Object.prototype.hasOwnProperty.call(fns, 'homeSignals') ? fns.homeSignals : null
    if (typeof own !== 'function') continue
    const short = appId.split('/').pop().replace(/-app$/, '')
    const stateRoot = (getState && getState(appId)) || r
    // The module function runs as an element of ITS app: `this.call` resolves
    // the app's own functions first, `this.state.root` is its state root.
    const shim = {
      key: 'homeSignals',
      context: modCtx,
      sdk: (el && el.sdk) || null,
      state: { root: stateRoot },
      call: (name, ...args) => {
        const f = modCtx.functions[name]
        return typeof f === 'function' ? f.apply(shim, args) : undefined
      }
    }
    providers.push({
      app: short,
      appId,
      ready: true,
      run: () => own.call(shim, ctxFor({ app: short, appId, root: stateRoot, shell: r }))
    })
  }
  return {
    known: !settingsPending && appsKnown && moduleKnown && !holding,
    providers,
    failed: failed.slice().sort()
  }
}

// ── One pass ─────────────────────────────────────────────────────────────────
// Called on the greeting head's render, on its sources changing and on its
// clock. Runs every provider whose answer is missing or older than 60 s,
// waits at most 800 ms, then closes the pass window. Returns { nextAt } — when
// the next pass is worth running (a refresh, a hang limit, a readiness cap).
// `opts` are test seams: { now, budget, ttl, hang, wait, sleep }.
export const homeSignalsTick = async function homeSignalsTick(root, opts) {
  const el = this
  const o = opts && typeof opts === 'object' ? opts : {}
  const now = typeof o.now === 'function' ? o.now : () => Date.now()
  const BUDGET = typeof o.budget === 'number' ? o.budget : 800
  const TTL = typeof o.ttl === 'number' ? o.ttl : 60000
  const HANG = typeof o.hang === 'number' ? o.hang : 10000
  const WAIT = typeof o.wait === 'number' ? o.wait : 5000
  const sleep =
    typeof o.sleep === 'function'
      ? o.sleep
      : (ms) =>
          new Promise((resolve) => {
            const t = setTimeout(resolve, ms)
            if (t && typeof t.unref === 'function') t.unref()
          })
  const r = root || (el && el.state && el.state.root)
  if (!r || typeof r.update !== 'function' || !el || typeof el.call !== 'function') return null
  const wsOf = () => {
    const w = r.workspace
    return (w && String(w.id || w._id || '')) || null
  }
  const ws = wsOf()
  if (!ws) return null
  const read = () => {
    const cur = r._homeSignals
    return cur && typeof cur === 'object' && cur.ws === ws ? cur : null
  }
  const fresh = (t) => ({
    ws,
    known: false,
    expected: [],
    failed: [],
    apps: {},
    inflight: {},
    logged: {},
    firstAt: t,
    windowAt: 0,
    updatedAt: 0
  })
  // Every write re-reads the live key and merges, so answers landing in any
  // order never overwrite each other; a switch (another workspace on screen)
  // drops the write.
  const write = (fn) => {
    if (wsOf() !== ws) return false
    const cur = read() || fresh(now())
    const next = fn(cur)
    if (!next || next === cur) return false
    r.update({ _homeSignals: next }, { preventFetch: true })
    return true
  }

  let listing = null
  try {
    listing = el.call('homeSignalsProviders', r)
  } catch (err) {
    console.warn('[home] signals providers unavailable:', err && err.message)
  }
  if (!listing || typeof listing !== 'object') listing = { known: false, providers: [] }
  const providers = Array.isArray(listing.providers) ? listing.providers : []
  const failedNow = Array.isArray(listing.failed) ? listing.failed : []
  const t0 = now()
  const before = read() || fresh(t0)
  const expected = providers.map((p) => p.app)

  // A call out for longer than the hang limit reads as an empty answer (and
  // logs once); it is retried on the next pass.
  const hung = Object.keys(before.inflight || {}).filter(
    (app) => t0 - before.inflight[app] >= HANG && expected.includes(app)
  )
  const due = providers.filter((p) => {
    const hit = before.apps[p.app]
    if (hit && t0 - hit.at < TTL) return false
    const started = before.inflight[p.app]
    if (started && t0 - started < HANG) return false
    return !!p.ready || t0 - before.firstAt >= WAIT
  })

  write((c) => {
    const same =
      !hung.length &&
      !due.length &&
      c.known === !!listing.known &&
      c.expected.length === expected.length &&
      c.expected.every((a, i) => a === expected[i]) &&
      (c.failed || []).join('|') === failedNow.join('|')
    if (same && read()) return c
    const inflight = { ...c.inflight }
    const apps = { ...c.apps }
    const logged = { ...c.logged }
    for (const app of hung) {
      delete inflight[app]
      if (!apps[app]) apps[app] = { at: t0, status: 'timeout', signals: [] }
      if (!logged[app]) {
        logged[app] = true
        console.warn('[home] signals provider did not answer in time:', app)
      }
    }
    for (const p of due) inflight[p.app] = t0
    return { ...c, known: !!listing.known, expected, failed: failedNow, apps, inflight, logged }
  })

  const runs = due.map((p) =>
    Promise.resolve()
      .then(() => p.run())
      .then(
        (list) => ({ ok: true, list }),
        (err) => ({ ok: false, err })
      )
      .then((res) => {
        const signals = res.ok ? el.call('homeSignalsNormalize', res.list, p.app) : []
        const t = now()
        write((c) => {
          const inflight = { ...c.inflight }
          delete inflight[p.app]
          const logged = { ...c.logged }
          if (!res.ok && !logged[p.app]) {
            logged[p.app] = true
            console.warn(
              '[home] signals provider failed — read as none:',
              p.app,
              res.err && res.err.message
            )
          }
          return {
            ...c,
            apps: { ...c.apps, [p.app]: { at: t, status: res.ok ? 'ok' : 'error', signals } },
            inflight,
            logged,
            updatedAt: t
          }
        })
      })
  )
  if (runs.length) await Promise.race([Promise.all(runs), sleep(BUDGET)])
  // The first pass window closes: from here the line shows what answered.
  write((c) => (c.windowAt ? c : { ...c, windowAt: now() }))

  // When a next pass is worth running.
  const after = read() || fresh(t0)
  const times = []
  for (const app of expected) {
    const hit = after.apps[app]
    if (hit) times.push(hit.at + TTL)
    const started = after.inflight[app]
    if (started) times.push(started + HANG)
  }
  if (providers.some((p) => !p.ready && !after.apps[p.app])) times.push(after.firstAt + WAIT)
  return { nextAt: times.length ? Math.min(...times) : null }
}

// ── Refresh at once ──────────────────────────────────────────────────────────
// `el.call('homeSignalsInvalidate', app)` from an app's mutation site (a
// follow-up done, a mail read): the app's answer goes stale and a pass runs.
// No app = every provider (the summary's Refresh).
export const homeSignalsInvalidate = function homeSignalsInvalidate(app, opts) {
  const el = this
  const r = el && el.state && el.state.root
  if (!r || typeof r.update !== 'function') return null
  const cur = r._homeSignals
  if (cur && typeof cur === 'object' && cur.apps) {
    const apps = {}
    for (const [k, v] of Object.entries(cur.apps)) {
      apps[k] = !app || k === app ? { ...v, at: 0 } : v
    }
    r.update({ _homeSignals: { ...cur, apps } }, { preventFetch: true })
  }
  return typeof el.call === 'function' ? el.call('homeSignalsTick', r, opts) : null
}

// ── The view the greeting and the summary read ──────────────────────────────
// → { list, settled, waiting, failed, updatedAt }. `list` is every answered signal in
// the shell order; `settled` = the providers are known and each has answered;
// `waiting` = the first pass window is still open, OR the apps are not known
// yet (an installed app still loading or setting up — e86c73613), OR nothing
// has answered and not every provider has. While the apps are not known the
// line and the summary hold their skeleton — never a premature "nothing" and
// never a partial summary that a setting-up app would change a moment later
// (crm-w1's live finding, dev 37211a590: "Nothing needs you right now." for
// 8.5 s before "49 follow-ups overdue"). Reads only root state, so a reactive
// caller re-runs when an answer lands.
export const homeSignalsView = function homeSignalsView(root) {
  const RANK = { urgent: 0, attention: 1, info: 2 }
  const r = root && typeof root === 'object' ? root : {}
  const w = r.workspace
  const ws = (w && String(w.id || w._id || '')) || null
  const st = r._homeSignals
  if (!ws || !st || typeof st !== 'object' || st.ws !== ws) {
    return { list: [], settled: false, waiting: true, updatedAt: 0 }
  }
  const expected = Array.isArray(st.expected) ? st.expected : []
  const apps = st.apps && typeof st.apps === 'object' ? st.apps : {}
  const settled = !!st.known && expected.every((a) => !!apps[a])
  const all = []
  // ONE FACT, ONCE: a signal's `id` is namespaced by the module that emits it
  // (`crm.followups.dueToday`), so the same id from two providers is the same
  // fact — an app whose module context carries the CRM's provider beside the
  // CRM itself printed "2 follow-ups due today" twice in the greeting (the
  // media lane, dev 2026-10-25). The first provider in install order keeps it.
  const seenIds = new Set()
  for (const a of expected) {
    const hit = apps[a]
    if (!hit || !Array.isArray(hit.signals)) continue
    for (const s of hit.signals) {
      const id = s && typeof s.id === 'string' && s.id ? s.id : null
      if (id && seenIds.has(id)) continue
      if (id) seenIds.add(id)
      all.push(s)
    }
  }
  // The App strip order: a built-in app's shortcut, a module app's first
  // icon-only widget, in the saved home layout.
  const layout = Array.isArray(r._homeLayout) ? r._homeLayout : []
  const pos = (id) => {
    const at = layout.findIndex((e) => e && e.id === id)
    return at < 0 ? Number.MAX_SAFE_INTEGER : at
  }
  const order = { tasks: pos('app-tickets'), calendar: pos('app-calendar') }
  const modules = []
  if (r.workspaceProject && r.workspaceProject.id) modules.push(r.workspaceProject)
  for (const a of Array.isArray(r.workspaceApps) ? r.workspaceApps : []) modules.push(a)
  for (const m of modules) {
    const short = String(m.id || '')
      .split('/')
      .pop()
      .replace(/-app$/, '')
    if (!short || short in order) continue
    const icons = (Array.isArray(m.widgets) ? m.widgets : []).filter(
      (x) => x && (x.kind === 'app' || (x.spec && x.spec.iconOnly))
    )
    order[short] = icons.length ? Math.min(...icons.map((x) => pos(x.id))) : Number.MAX_SAFE_INTEGER
  }
  const place = (app) =>
    Object.prototype.hasOwnProperty.call(order, app) ? order[app] : Number.MAX_SAFE_INTEGER
  const list = all
    .map((s, i) => ({ s, i }))
    .sort(
      (a, b) =>
        (RANK[a.s.severity] ?? 3) - (RANK[b.s.severity] ?? 3) ||
        b.s.count - a.s.count ||
        place(a.s.app) - place(b.s.app) ||
        a.i - b.i
    )
    .map((x) => x.s)
  const waiting = !st.windowAt || !st.known || (!list.length && !settled)
  // How many installed apps the loader says will not come (known without
  // data): with zero items the home may not say "nothing" over them.
  const failed = Array.isArray(st.failed) ? st.failed.length : 0
  return { list, settled, waiting, failed, updatedAt: st.updatedAt || 0 }
}

// ── A source of a built-in provider changed → its answer goes stale ─────────
// The greeting head's `stateDeps` are [tickets, events, mail count, apps,
// workspace]; `change` is the framework's { prev, next } of those. A changed
// source re-reads its own app at once; an apps / workspace change runs a pass.
export const homeSignalsOnData = function homeSignalsOnData(el, s, change) {
  const self = el || this
  if (!self || typeof self.call !== 'function') return null
  const root = s && s.root
  const prev = (change && change.prev) || []
  const next = (change && change.next) || []
  // each built-in app's place in the head's deps (pages/main.js HomeGreeting:
  // tickets, events, mail count, apps, project, workspace, unread
  // notifications, notifications loaded)
  const builtIns = { tasks: [0], calendar: [1], mail: [2], inbox: [6, 7] }
  const stale = Object.keys(builtIns).filter((app) =>
    builtIns[app].some((i) => prev[i] !== next[i])
  )
  const cur = root && root._homeSignals
  if (
    stale.length &&
    cur &&
    typeof cur === 'object' &&
    cur.apps &&
    typeof root.update === 'function'
  ) {
    const apps = { ...cur.apps }
    for (const app of stale) if (apps[app]) apps[app] = { ...apps[app], at: 0 }
    root.update({ _homeSignals: { ...cur, apps } }, { preventFetch: true })
  }
  return self.call('homeSignalsTick', root)
}

// ── The built-in providers ───────────────────────────────────────────────────
// Each answers `homeSignals(ctx)` over the SHELL root (`ctx.root`).

// Tasks: the open tasks assigned to me, and the overdue ones among them (a due
// date before today). The open set is the Received-tasks widget's: the home's
// own ticket cache, the board cache only until it loads.
export const homeSignalsTasks = function homeSignalsTasks(ctx) {
  const root = (ctx && ctx.root) || {}
  const done = ['done', 'closed', 'resolved', 'cancelled', 'archived']
  const me = String(root.currentUserEmail || '').toLowerCase()
  if (!me) return []
  const rows =
    Array.isArray(root._userTickets) && root._userTickets.length
      ? root._userTickets
      : Array.isArray(root.tickets)
        ? root.tickets
        : []
  const mine = rows.filter((t) => {
    if (!t || done.includes(String(t.state || t.status || '').toLowerCase())) return false
    return String(t.assigneeEmail || t.assignee_email || '').toLowerCase() === me
  })
  const nowMs = (ctx && ctx.now) || Date.now()
  const today = new Date(nowMs)
  today.setHours(0, 0, 0, 0)
  const overdue = mine.filter((t) => {
    const due = t.dueDate || t.due_date
    if (!due) return false
    const d = new Date(due)
    if (Number.isNaN(d.getTime())) return false
    // A bare YYYY-MM-DD is a LOCAL calendar day.
    const day = /^\d{4}-\d{2}-\d{2}$/.test(String(due))
      ? new Date(
          Number(String(due).slice(0, 4)),
          Number(String(due).slice(5, 7)) - 1,
          Number(String(due).slice(8, 10))
        )
      : d
    return day.getTime() < today.getTime()
  }).length
  const out = []
  if (overdue) {
    out.push({
      id: 'tasks.mine.overdue',
      app: 'tasks',
      kind: 'overdue',
      count: overdue,
      severity: 'urgent',
      label: {
        key:
          overdue === 1
            ? 'workspace.home.signals.taskOverdueOne'
            : 'workspace.home.signals.taskOverdueOther',
        params: { count: overdue }
      },
      href: '/tasks/my'
    })
  }
  if (mine.length) {
    out.push({
      id: 'tasks.mine.waiting',
      app: 'tasks',
      kind: 'waiting',
      count: mine.length,
      severity: 'info',
      label: {
        key:
          mine.length === 1 ? 'workspace.home.summary.taskOne' : 'workspace.home.summary.taskOther',
        params: { count: mine.length }
      },
      href: '/tasks/my'
    })
  }
  return out
}

// Calendar: my events still ahead today; `at` is the nearest one's start.
export const homeSignalsCalendar = function homeSignalsCalendar(ctx) {
  const root = (ctx && ctx.root) || {}
  const me = root.currentUserEmail
  const nowMs = (ctx && ctx.now) || Date.now()
  const start = new Date(nowMs)
  start.setHours(0, 0, 0, 0)
  const end = start.getTime() + 24 * 60 * 60 * 1000
  const ahead = (Array.isArray(root.intranetEvents) ? root.intranetEvents : [])
    .filter((e) => {
      const t = e && e.startAt ? new Date(e.startAt).getTime() : 0
      if (!t || t < nowMs || t >= end) return false
      return !Array.isArray(e.attendees) || e.attendees.includes(me)
    })
    .map((e) => new Date(e.startAt).getTime())
    .sort((a, b) => a - b)
  if (!ahead.length) return []
  return [
    {
      id: 'calendar.events.dueToday',
      app: 'calendar',
      kind: 'dueToday',
      count: ahead.length,
      severity: 'attention',
      label: {
        key:
          ahead.length === 1
            ? 'workspace.home.summary.eventOne'
            : 'workspace.home.summary.eventOther',
        params: { count: ahead.length }
      },
      href: '/calendar',
      at: new Date(ahead[0]).toISOString()
    }
  ]
}

// Mail: unread across the accounts this member can read (the badge's count).
export const homeSignalsMail = function homeSignalsMail(ctx) {
  const root = (ctx && ctx.root) || {}
  const counts =
    root.navUnreadCounts && typeof root.navUnreadCounts === 'object' ? root.navUnreadCounts : {}
  const n = Math.floor(Number(counts.mail || 0))
  if (!n || n < 1) return []
  return [
    {
      id: 'mail.inbox.new',
      app: 'mail',
      kind: 'new',
      count: n,
      severity: 'info',
      label: {
        key: n === 1 ? 'workspace.home.summary.mailOne' : 'workspace.home.summary.mailOther',
        params: { count: n }
      },
      href: '/mail'
    }
  ]
}

// Inbox: the unread notifications that belong to this workspace (the bell's
// Workspace rule, shared notificationScope.js isCurrentWorkspaceNotification —
// the centre holds the person's rows of every workspace). Counts only: no
// title or body rides the signal. Opens the Inbox ON ITS WORKSPACE FILTER: the
// bell, the Menu dot and the Inbox count the unread rows of EVERY workspace,
// so the card's "63 unread notifications" stood beside their 93 for one thing
// (CHECK 8 on dev, W25 7b8863011, 2026-10-30). Nika 2026-10-30: the words name
// the scope ("… in this workspace"), and the open lands where the counted rows
// are listed (`notifFilter`, applied by homeSignalsOpen).
export const homeSignalsInbox = function homeSignalsInbox(ctx) {
  const el = this
  const root = (ctx && ctx.root) || {}
  const rows = Array.isArray(root.notifs) ? root.notifs : []
  const mine = (n) => {
    if (!el || typeof el.call !== 'function') return true
    return el.call('isCurrentWorkspaceNotification', n, root) !== false
  }
  const n = rows.filter((row) => row && !row.read && mine(row)).length
  if (!n) return []
  return [
    {
      id: 'inbox.notifications.new',
      app: 'inbox',
      kind: 'new',
      count: n,
      severity: 'attention',
      label: {
        key: n === 1 ? 'workspace.home.summary.notifOne' : 'workspace.home.summary.notifOther',
        params: { count: n }
      },
      href: '/inbox',
      notifFilter: 'workspace'
    }
  ]
}

// ── Open a signal's list ─────────────────────────────────────────────────────
// A link, a chip, a tile: the shell's ONE slot-or-page decision (`openInSlot`).
// A modified click (a new tab or window) is the browser's own.
// A signal that carries the Inbox filter of what it counted (`notifFilter`:
// the inbox signal's 'workspace') sets the root's `_notifFilter` FIRST, so the
// Inbox opens on the rows the card counted (Nika 2026-10-30, see
// homeSignalsInbox) — the same root key the Inbox tabs and the bell write. Read
// as the signal's OWN key only: an element state reads a key it does not hold
// from the ROOT state (SYNTAX, "falls back to the ROOT state"; `in` leaves the
// fallback out), so a mail or tasks tile never writes the filter.
export const homeSignalsOpen = function homeSignalsOpen(e, sig) {
  const el = this
  const NOTIF_FILTERS = ['all', 'unread', 'workspace']
  const href = sig && typeof sig.href === 'string' ? sig.href : null
  if (!href) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault()
    return false
  }
  if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1)) return false
  if (e && typeof e.preventDefault === 'function') e.preventDefault()
  if (e && typeof e.stopPropagation === 'function') e.stopPropagation()
  const filter =
    typeof sig === 'object' && 'notifFilter' in sig && NOTIF_FILTERS.includes(sig.notifFilter)
      ? sig.notifFilter
      : null
  const root = el && el.state && el.state.root
  if (filter && root && typeof root.update === 'function') {
    root.update({ _notifFilter: filter }, { preventFetch: true })
  }
  if (el && typeof el.call === 'function') el.call('openInSlot', href, el)
  return true
}

// ── The home top's clock ─────────────────────────────────────────────────────
// Bound to the greeting head (its own `state: { now }`): every 30 s the head's
// clock moves — the greeting's hour band and the date follow the wall clock —
// the device copy of the look mirrors the record, and a signals pass runs
// (fresh answers are not re-asked; stale ones refresh). When a pass names an
// earlier moment worth a look (a provider's readiness cap, a hang limit), one
// timer takes it. A background tab throttles the interval to once a minute,
// which is all a minute clock needs. Stopped when the head leaves the page.
export const homeTopClock = function homeTopClock() {
  const el = this
  if (!el || el.__homeTopClock || typeof setInterval !== 'function') return
  const rootOf = () => (el.state && el.state.root) || null
  const schedule = (pass) =>
    Promise.resolve(pass)
      .then((res) => {
        const at = res && res.nextAt
        if (!at || !el.node) return
        const wait = Math.max(1000, at - Date.now())
        if (wait >= 30000) return
        clearTimeout(el.__homeTopSoon)
        el.__homeTopSoon = setTimeout(() => schedule(el.call('homeSignalsTick', rootOf())), wait)
        if (el.__homeTopSoon && typeof el.__homeTopSoon.unref === 'function')
          el.__homeTopSoon.unref()
      })
      .catch((err) => console.warn('[home] signals pass failed:', err && err.message))
  const beat = () => {
    if (!el.node || !el.state || typeof el.state.update !== 'function') {
      clearInterval(el.__homeTopClock)
      el.__homeTopClock = null
      return
    }
    el.state.update({ now: Date.now() }, { preventFetch: true })
    el.call('homeLookSyncCache', rootOf())
    schedule(el.call('homeSignalsTick', rootOf()))
  }
  el.__homeTopClock = setInterval(beat, 30000)
  // Node (the jsdom tests) must not wait on the clock to exit.
  if (el.__homeTopClock && typeof el.__homeTopClock.unref === 'function') el.__homeTopClock.unref()
  el.call('homeLookSyncCache', rootOf())
  schedule(el.call('homeSignalsTick', rootOf()))
}

export const homeTopClockStop = function homeTopClockStop() {
  const el = this
  if (!el) return
  if (el.__homeTopClock) {
    clearInterval(el.__homeTopClock)
    el.__homeTopClock = null
  }
  if (el.__homeTopSoon) {
    clearTimeout(el.__homeTopSoon)
    el.__homeTopSoon = null
  }
}
